/* eslint-disable max-len */
import {
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import type {
  OnCreateEntityAuthSubscription,
  OnCreateEntitySubscription,
  OnDeleteEntityAuthSubscription,
  OnDeleteEntitySubscription,
  OnUpdateEntityAuthSubscription,
  OnUpdateEntitySubscription,
} from '@/infra/appsync';
import type { AppDispatch, ThunkApiType } from '@/store';

import { attendContactSlice } from '@/domain/attendContact/redux';
import { attendRegisterSlice } from '@/domain/attendRegister/redux';
import { childSlice } from '@/domain/child/redux';
import { childApplySlice } from '@/domain/childApply/redux';
import { childNotebookSlice } from '@/domain/childNotebook/redux';
import { classSlice } from '@/domain/class/redux';
import { defecationSlice } from '@/domain/defecation/redux';
import { drugSlice } from '@/domain/drug/redux';
import { mealSlice } from '@/domain/meal/redux';
import { measureSlice } from '@/domain/measure/redux';
import { milkSlice } from '@/domain/milk/redux';
import { newsSlice } from '@/domain/news/redux';
import { photoSlice } from '@/domain/photo/redux';
import { sleepSlice } from '@/domain/sleep/redux';
import { temperatureSlice } from '@/domain/temperature/redux';
import {
  EntityName,
  OnCreateEntityAuthDocument,
  OnCreateEntityDocument,
  OnDeleteEntityAuthDocument,
  OnDeleteEntityDocument,
  OnUpdateEntityAuthDocument,
  OnUpdateEntityDocument,
} from '@/infra/appsync';
import { api, service } from '@/share';

const refleshFacilityHomepageSettingsList = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'childApply/refleshFacilityHomepageSettingsList',
  async (_, thunkApi) => {
    const item = thunkApi.getState().childApply;
    const result = await service.childApply.getFacilityHomepageSettingsList(item.facilityList);
    thunkApi.dispatch(childApplySlice.actions.setFacilityHomepageSettingsList(result));
  },
);

const subscribeCreateEntity = createAsyncThunk<
  void,
  OnCreateEntitySubscription,
  ThunkApiType
>(
  'user/subscribeCreateEntity',
  (entity, thunkApi) => {
    const item = entity.onCreateEntity;
    if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildApplyEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.createChildApplyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildChildEntity as string)) {
      thunkApi.dispatch(childSlice.actions.createChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.createSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.createMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.createMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.createDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.createTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.createDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.createMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.createSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.createAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildRemoveEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.createChildRemoveStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.createPageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDeviceDeviceEntity as string)) {
      // skip
    } else {
      // eslint-disable-next-line no-console
      console.error('実装してください(OnCreateEntitySubscription)', item);
    }
  },
);

const subscribeCreateEntityAuth = createAsyncThunk<
  void,
  OnCreateEntityAuthSubscription,
  ThunkApiType
>(
  'user/subscribeCreateEntityAuth',
  (entity, thunkApi) => {
    const item = entity.onCreateEntityAuth;
    if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.createSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildApplyEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.createChildApplyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildApplyFacilityEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.createFacilityStore(item));
      thunkApi.dispatch(refleshFacilityHomepageSettingsList());
    } else if (item?.key.startsWith(EntityName.WagakoNurseryClassClassChildEntity as string)) {
      thunkApi.dispatch(classSlice.actions.createClassChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactAbsenceEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.createAttendContactAbsenceStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLateEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.createAttendContactLateStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLeaveEarlyEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.createAttendContactLeaveEarlyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.createMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.createMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.createDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.createTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.createDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.createMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.createSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.createSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.createPageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.createAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendanceSheetEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.createSheetStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildRemoveEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.createChildRemoveStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildTimeDivisionEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildExtendedChildcareEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryDefecationDefecationEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryMealMealEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryDrugDrugEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryMilkMilkEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurserySleepSleepWakeupEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryTemperatureTemperatureEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryPhotoPhotoOrderEntity as string)) {
      thunkApi.dispatch(photoSlice.actions.createOrderStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryPhotoPhotoSalesEntity as string)) {
      // Skip
    } else {
      api.wagakoAppApi.errorReport({
        title: '実装してください(OnCreateEntityAuthSubscription)',
        content: JSON.stringify(item),
      });
    }
  },
);

const subscribeUpdateEntity = createAsyncThunk<
  void,
  OnUpdateEntitySubscription,
  ThunkApiType
>(
  'user/subscribeUpdateEntity',
  (entity, thunkApi) => {
    const item = entity.onUpdateEntity;
    if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildApplyEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.updateChildApplyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildChildEntity as string)) {
      thunkApi.dispatch(childSlice.actions.updateChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.updateSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.updateMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.updateMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.updateDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.updateTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.updateDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.updateMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.updateSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.updatePageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.updateAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDeviceDeviceEntity as string)) {
      // skip
    } else {
      api.wagakoAppApi.errorReport({
        title: '実装してください(OnUpdateEntitySubscription)',
        content: JSON.stringify(item),
      });
    }
  },
);

const subscribeUpdateEntityAuth = createAsyncThunk<
  void,
  OnUpdateEntityAuthSubscription,
  ThunkApiType
>(
  'user/subscribeUpdateEntityAuth',
  (entity, thunkApi) => {
    const item = entity.onUpdateEntityAuth;
    if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildApplyEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.updateChildApplyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildChildEntity)) {
      thunkApi.dispatch(childSlice.actions.updateChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.updateSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryClassClassChildEntity as string)) {
      thunkApi.dispatch(classSlice.actions.updateClassChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactAbsenceEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.updateAttendContactAbsenceStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLateEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.updateAttendContactLateStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLeaveEarlyEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.updateAttendContactLeaveEarlyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.updateMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.updateMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.updateDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.updateTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.updateDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.updateMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.updateSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.updateSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.updatePageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.updateAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendanceSheetEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.updateSheetStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppUserUserEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoAppChildApplyFacilityEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.updateFacilityStore(item));
      thunkApi.dispatch(refleshFacilityHomepageSettingsList());
    } else if (item?.key.startsWith(EntityName.WagakoNurseryPhotoPhotoOrderEntity as string)) {
      thunkApi.dispatch(photoSlice.actions.updateOrderStore(item));
    } else {
      api.wagakoAppApi.errorReport({
        title: '実装してください(OnUpdateEntityAuthSubscription)',
        content: JSON.stringify(item),
      });
    }
  },
);

const subscribeDeleteEntity = createAsyncThunk<
  void,
  OnDeleteEntitySubscription,
  ThunkApiType
>(
  'user/subscribeDeleteEntity',
  (entity, thunkApi) => {
    const item = entity.onDeleteEntity;
    if (item?.key.startsWith(EntityName.WagakoNurseryChildApplyChildApplyEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.deleteChildApplyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildChildEntity)) {
      thunkApi.dispatch(childSlice.actions.deleteChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.deleteSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.deleteMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.deleteMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.deleteDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.deleteTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.deleteDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.deleteMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.deleteSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.deletePageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.deleteAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildApplyFacilityEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.deleteFacilityStore(item));
      thunkApi.dispatch(refleshFacilityHomepageSettingsList());
    } else if (item?.key.startsWith(EntityName.WagakoAppDeviceDeviceEntity as string)) {
      // skip
    } else {
      api.wagakoAppApi.errorReport({
        title: '実装してください(OnDeleteEntitySubscription)',
        content: JSON.stringify(item),
      });
    }
  },
);

const subscribeDeleteEntityAuth = createAsyncThunk<
  void,
  OnDeleteEntityAuthSubscription,
  ThunkApiType
>(
  'user/subscribeDeleteEntityAuth',
  (entity, thunkApi) => {
    const item = entity.onDeleteEntityAuth;
    if (item?.key.startsWith(EntityName.WagakoAppNewsSentNewsEntity as string)) {
      thunkApi.dispatch(newsSlice.actions.deleteSentNewsStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryClassClassChildEntity as string)) {
      thunkApi.dispatch(classSlice.actions.deleteClassChildStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactAbsenceEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.deleteAttendContactAbsenceStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLateEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.deleteAttendContactLateStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendContactAttendContactLeaveEarlyEntity as string)) {
      thunkApi.dispatch(attendContactSlice.actions.deleteAttendContactLeaveEarlyStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMeasureMeasureEntity as string)) {
      thunkApi.dispatch(measureSlice.actions.deleteMeasureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppMealMealEntity as string)) {
      thunkApi.dispatch(mealSlice.actions.deleteMealStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteMealStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppDrugDrugEntity as string)) {
      thunkApi.dispatch(drugSlice.actions.deleteDrugStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteDrugStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryMealMealEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryDrugDrugEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoAppTemperatureTemperatureEntity as string)) {
      thunkApi.dispatch(temperatureSlice.actions.deleteTemperatureStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteTemperatureStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryTemperatureTemperatureEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoAppDefecationDefecationEntity as string)) {
      thunkApi.dispatch(defecationSlice.actions.deleteDefecationStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteDefecationStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryDefecationDefecationEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoAppMilkMilkEntity as string)) {
      thunkApi.dispatch(milkSlice.actions.deleteMilkStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteMilkStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppSleepSleepWakeupEntity as string)) {
      thunkApi.dispatch(sleepSlice.actions.deleteSleepStore(item));
      thunkApi.dispatch(childNotebookSlice.actions.deleteSleepStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildNotebookPageEntity as string)) {
      thunkApi.dispatch(childNotebookSlice.actions.deletePageStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryMilkMilkEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurserySleepSleepWakeupEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.deleteAttendRegisterStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoNurseryAttendanceSheetEntity as string)) {
      thunkApi.dispatch(attendRegisterSlice.actions.deleteSheetStore(item));
    } else if (item?.key.startsWith(EntityName.WagakoAppChildApplyFacilityEntity as string)) {
      thunkApi.dispatch(childApplySlice.actions.deleteFacilityStore(item));
      thunkApi.dispatch(refleshFacilityHomepageSettingsList());
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildExtendedChildcareEntity as string)) {
      // Skip
    } else if (item?.key.startsWith(EntityName.WagakoNurseryChildTimeDivisionEntity as string)) {
      // Skip
    } else {
      api.wagakoAppApi.errorReport({
        title: '実装してください(OnDeleteEntityAuthSubscription)',
        content: JSON.stringify(item),
      });
    }
  },
);

/**
 * サブスクリプション
 */
export type SubscribeType<T> = {
  /** ID */
  id: string;
  /** クエリ */
  query: string;
  /** 変数 */
  variables: {
    /** ID */
    id: string;
  };
  /** サブスクリプション動作 */
  onSubscribe: (msg: {
    /** payload */
    payload: {
      /** データ */
      data: T,
    },
  }) => Promise<void>;
}

/**
 * サブスクリプション
 */
export type SubscribeTypes =
  SubscribeType<OnCreateEntitySubscription>
  | SubscribeType<OnCreateEntityAuthSubscription>
  | SubscribeType<OnUpdateEntitySubscription>
  | SubscribeType<OnUpdateEntityAuthSubscription>
  | SubscribeType<OnDeleteEntitySubscription>
  | SubscribeType<OnDeleteEntityAuthSubscription>;

/**
 * サブスクリプションリスト取得
 * @param username ユーザーID
 * @param dispatch Dispatch
 * @returns リスト
 */
export const getSubscribeList = (username: string, dispatch: AppDispatch): SubscribeTypes[] => [{
  id: `create_${username}-${uuidv4()}`,
  query: OnCreateEntityDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnCreateEntitySubscription; }; }) => {
    await dispatch(subscribeCreateEntity(
      msg.payload.data,
    ));
  },
}, {
  id: `create-auth_${username}-${uuidv4()}`,
  query: OnCreateEntityAuthDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnCreateEntityAuthSubscription; }; }) => {
    await dispatch(subscribeCreateEntityAuth(
      msg.payload.data,
    ));
  },
}, {
  id: `update_${username}-${uuidv4()}`,
  query: OnUpdateEntityDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnUpdateEntitySubscription; }; }) => {
    await dispatch(subscribeUpdateEntity(
      msg.payload.data,
    ));
  },
}, {
  id: `update-auth_${username}-${uuidv4()}`,
  query: OnUpdateEntityAuthDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnUpdateEntityAuthSubscription; }; }) => {
    await dispatch(subscribeUpdateEntityAuth(
      msg.payload.data,
    ));
  },
}, {
  id: `delete_${username}-${uuidv4()}`,
  query: OnDeleteEntityDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnDeleteEntitySubscription; }; }) => {
    await dispatch(subscribeDeleteEntity(
      msg.payload.data,
    ));
  },
}, {
  id: `delete-auth_${username}-${uuidv4()}`,
  query: OnDeleteEntityAuthDocument.loc?.source.body || '',
  variables: { id: username },
  onSubscribe: async (msg: { payload: { data: OnDeleteEntityAuthSubscription; }; }) => {
    await dispatch(subscribeDeleteEntityAuth(
      msg.payload.data,
    ));
  },
}];
